import { FC, useState } from "react";
import { Badge, Fade, Form, ListGroup } from "react-bootstrap";
import { CuttlefishBuild } from "../../../models/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { CuttlefishNetworkListItem } from "../../selectable/cuttlefish/CuttlefishNetworkListItem";
import styles from "../../modals/CuttlefishBuildModal.module.scss";

interface Props {
    network: string;
    isPartial: boolean;
    isAllEnabled: boolean;
    useDarkMode: boolean;
    networkBuilds: CuttlefishBuild[];
    visibleBuilds: CuttlefishBuild[];
    toggleBuild: (id: number) => void;
    toggleNetwork: (network: string) => void;
    toggleTag: (network: string, tag: string) => void;
    toggleSize: (network: string, size: number) => void;
}

export const CuttlefishNetworkList: FC<Props> = ({
    network,
    isPartial,
    isAllEnabled,
    useDarkMode,
    networkBuilds,
    visibleBuilds,
    toggleBuild,
    toggleNetwork,
    toggleTag,
    toggleSize
}) => {
    const [showChildren, updateShowChildren] = useState(false);
    const enabledCount = networkBuilds.filter(b => b.enabled).length;
    return (
        <div>
            <div
                className={`d-flex align-items-center flex-wrap gap-2 px-2 py-1 mb-1 rounded ${
                    useDarkMode
                        ? styles.networkHeaderDark
                        : styles.networkHeaderLight
                }`}
            >
                <Form.Check
                    type="checkbox"
                    id={`network-toggle-${network}`}
                    checked={isAllEnabled}
                    title="Select all from this network"
                    ref={(element: HTMLInputElement | null) => {
                        if (element) {
                            element.indeterminate = isPartial;
                        }
                    }}
                    onChange={() => toggleNetwork(network)}
                    label={
                        <span className="fw-semibold text-capitalize d-flex align-items-center gap-2">
                            {network}
                            <Badge
                                bg={
                                    isAllEnabled
                                        ? "success"
                                        : isPartial
                                          ? "warning"
                                          : "secondary"
                                }
                                className={`ms-1 ${styles.countBadge}`}
                                title={`${enabledCount}/${networkBuilds.length} selected`}
                            >
                                {enabledCount}/{networkBuilds.length}
                            </Badge>
                        </span>
                    }
                />
                {visibleBuilds.length !== networkBuilds.length ? (
                    <span
                        className={`small text-secondary ${styles.shownCount}`}
                    >
                        {visibleBuilds.length} shown
                    </span>
                ) : null}
                <FontAwesomeIcon
                    onClick={() => updateShowChildren(!showChildren)}
                    icon={showChildren ? faChevronUp : faChevronDown}
                    size="sm"
                    className="mouseHover"
                />
            </div>
            <Fade in={showChildren} mountOnEnter={true} unmountOnExit={true}>
                <div>
                    <ListGroup className="ms-1 ms-md-3 pb-2">
                        {visibleBuilds.map(build => (
                            <CuttlefishNetworkListItem
                                key={build.id}
                                build={build}
                                toggleBuild={toggleBuild}
                                toggleTag={toggleTag}
                                toggleSize={toggleSize}
                            />
                        ))}
                    </ListGroup>
                </div>
            </Fade>
        </div>
    );
};
